import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { publicApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Shield, Loader2, AlertTriangle, CreditCard, Ticket, ArrowLeft,
} from 'lucide-react';

/**
 * /checkin/pay
 * ─────────────────────────────────────────────────────────────────────────
 * Cover-charge payment handoff. Two paths:
 *
 *   1. **Online** — auto-POSTs to the configured gateway (CCAvenue / Paytm).
 *   2. **Pay at Gate** — customer enters a 6-digit code that gate staff
 *      generated in `/admin/gate-codes`. Backend validates code, amount
 *      matches, and marks check-in paid.
 */
export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId      = searchParams.get('order_id');
  const ref          = searchParams.get('ref');
  const isNewMember  = searchParams.get('new_member') === '1';

  const [session, setSession]         = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error,   setError]           = useState(null);

  // Method choice: null → show selector | 'online' | 'gate'
  const [method, setMethod] = useState(null);
  const [gateCode, setGateCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const autoPostRef = useRef(false);

  // ── Load payment + session ───────────────────────────────────────────
  useEffect(() => {
    if (!orderId) { navigate('/checkin'); return; }
    let alive = true;
    (async () => {
      try {
        const [statusRes, sessionRes] = await Promise.all([
          publicApi.getPaymentStatus(orderId),
          publicApi.getPaymentSession(orderId).catch((e) => {
            if (e?.response?.status === 409) return null;
            throw e;
          }),
        ]);
        if (!alive) return;
        setPaymentData(statusRes.data);
        if (sessionRes) setSession(sessionRes.data);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || 'Could not load payment details.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId, navigate]);

  // ── Already in terminal state? Route to status page ──────────────────
  useEffect(() => {
    if (!paymentData?.payment) return;
    const status = paymentData.payment.status;
    if (status === 'paid')      navigate(`/checkin/success?ref=${ref}&order_id=${orderId}${isNewMember ? '&new_member=1' : ''}`, { replace: true });
    else if (status === 'failed')    navigate(`/checkin/failure?ref=${ref}&order_id=${orderId}`, { replace: true });
    else if (status === 'cancelled') navigate(`/checkin/failure?ref=${ref}&order_id=${orderId}&reason=cancelled`, { replace: true });
  }, [paymentData, navigate, ref, orderId, isNewMember]);

  // ── Auto-POST to gateway when "online" is chosen ─────────────────────
  useEffect(() => {
    if (method !== 'online') return;
    if (!session || !session.gateway_url) return;
    if (autoPostRef.current) return;
    autoPostRef.current = true;

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = session.gateway_url;
    form.style.display = 'none';

    const addField = (name, value) => {
      const inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = name;
      inp.value = value;
      form.appendChild(inp);
    };
    if (session.gateway === 'paytm') {
      Object.entries(session.form_fields || {}).forEach(([k, v]) => addField(k, v));
    } else {
      autoPostRef.current = false;
      return;
    }
    document.body.appendChild(form);
    setTimeout(() => form.submit(), 600);
  }, [method, session]);

  // ── Submit gate code ─────────────────────────────────────────────────
  const submitGateCode = async (e) => {
    e?.preventDefault();
    if (!gateCode || gateCode.length !== 6) {
      toast.error('Please enter the full 6-digit code.');
      return;
    }
    if (!paymentData?.checkin?._id) {
      toast.error('Check-in not loaded yet. Please refresh.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicApi.redeemGateCode(paymentData.checkin._id, gateCode);
      // Backend marks the check-in as paid and creates a new GATE-… payment.
      // The original gateway order (CCAvenue/Paytm) was cancelled server-side,
      // so we route straight to the success page using the new gate order id.
      const newOrderId = res?.data?.payment?.order_id || `GATE-${gateCode}`;
      const refQs = paymentData?.checkin?.reference_id || ref || '';
      navigate(`/checkin/success?ref=${refQs}&order_id=${newOrderId}${isNewMember ? '&new_member=1' : ''}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not redeem code.');
      setSubmitting(false);
    }
  };

  // ── Loading / error states ───────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="text-center" data-testid="payment-loading">
          <Loader2 className="w-8 h-8 text-[#D4A843] animate-spin mx-auto mb-3" />
          <p className="text-[#B9B9B9] text-sm">Loading payment…</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="text-center max-w-sm px-4" data-testid="payment-error">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-[#F2F2F2] font-semibold mb-2">Couldn&apos;t load payment</p>
          <p className="text-[#B9B9B9] text-sm mb-4">{String(error)}</p>
          <Button onClick={() => navigate('/checkin')} className="btn-gold">Back to Check-in</Button>
        </div>
      </div>
    );
  }

  const amount       = paymentData?.payment?.amount || 0;
  const customerName = paymentData?.checkin?.customer_name || '';
  const gateway      = session?.gateway;
  const gatewayLabel = gateway === 'paytm' ? 'Paytm' : 'Secure Gateway';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }} data-testid="payment-page">
      {/* Header */}
      <div className="hero-gradient">
        <div className="max-w-[440px] mx-auto px-4 pt-8 pb-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
              <Shield size={11} className="text-[#120F08]" />
            </div>
            <span className="font-['EB_Garamond'] text-[#F2F2F2] text-base font-semibold">Scratch Unleashed</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[#D4A843]/40 flex items-center justify-center">
              <span className="text-[#D4A843] font-['EB_Garamond'] font-semibold">
                {customerName.charAt(0).toUpperCase() || '·'}
              </span>
            </div>
            <div>
              <p className="text-[#F2F2F2] font-semibold" data-testid="payment-customer-name">{customerName}</p>
              <p className="text-[#B9B9B9] text-sm">Ref: <span className="text-mono text-[#D4A843]" data-testid="payment-ref">{ref}</span></p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[440px] mx-auto px-4 py-4 space-y-4">
        <div className="surface-card card-highlight rounded-xl p-5 text-center">
          <p className="text-[#B9B9B9] text-sm mb-1">Cover Charge</p>
          <p className="font-['EB_Garamond'] text-4xl text-[#D4A843] font-semibold" data-testid="payment-amount">
            {formatCurrency(amount)}
          </p>
          <p className="text-[#6F6F6F] text-xs mt-1 text-mono" data-testid="payment-order-id">{orderId}</p>
        </div>

        {/* ── Method selector ─────────────────────────────────────────── */}
        {!method && (
          <div className="space-y-3" data-testid="payment-method-selector">
            <p className="text-[#B9B9B9] text-xs uppercase tracking-wider text-center">How would you like to pay?</p>

            <button
              onClick={() => setMethod('online')}
              disabled={!session || !session.gateway_url}
              className="w-full bg-[#1A1A1A] hover:bg-[#222] border border-[#3A2F16] rounded-xl p-4 flex items-center gap-3 text-left transition disabled:opacity-50"
              data-testid="method-online"
            >
              <div className="w-10 h-10 rounded-lg bg-[#D4A843]/15 flex items-center justify-center">
                <CreditCard size={18} className="text-[#D4A843]" />
              </div>
              <div className="flex-1">
                <p className="text-[#F2F2F2] font-semibold text-sm">Pay Online</p>
                <p className="text-[#6F6F6F] text-xs">{gatewayLabel} · UPI · Cards · Net Banking</p>
              </div>
            </button>

            <button
              onClick={() => setMethod('gate')}
              className="w-full bg-[#1A1A1A] hover:bg-[#222] border border-[#3A2F16] rounded-xl p-4 flex items-center gap-3 text-left transition"
              data-testid="method-gate"
            >
              <div className="w-10 h-10 rounded-lg bg-[#D4A843]/15 flex items-center justify-center">
                <Ticket size={18} className="text-[#D4A843]" />
              </div>
              <div className="flex-1">
                <p className="text-[#F2F2F2] font-semibold text-sm">Pay at Gate</p>
                <p className="text-[#6F6F6F] text-xs">Enter a code from the gate staff</p>
              </div>
            </button>
          </div>
        )}

        {/* ── Online → redirecting state ──────────────────────────────── */}
        {method === 'online' && (
          <div className="surface-card card-highlight rounded-xl p-6 text-center" data-testid="online-redirecting">
            <Loader2 className="w-12 h-12 text-[#D4A843] animate-spin mx-auto mb-4" />
            <p className="text-[#F2F2F2] font-semibold">Redirecting to {gatewayLabel}…</p>
            <p className="text-[#B9B9B9] text-sm mt-1">UPI · Cards · Net Banking · Wallets</p>
            <p className="text-[#6F6F6F] text-xs mt-3">Do not close this window.</p>
          </div>
        )}

        {/* ── Gate code entry ─────────────────────────────────────────── */}
        {method === 'gate' && (
          <form onSubmit={submitGateCode} className="surface-card card-highlight rounded-xl p-5 space-y-4" data-testid="gate-code-form">
            <button
              type="button"
              onClick={() => { setMethod(null); setGateCode(''); }}
              className="text-[#B9B9B9] text-xs flex items-center gap-1 hover:text-[#F2F2F2]"
              data-testid="gate-back-btn"
            >
              <ArrowLeft size={11} /> Back
            </button>

            <div>
              <p className="text-[#F2F2F2] font-semibold text-sm mb-1">Enter the 6-digit gate code</p>
              <p className="text-[#6F6F6F] text-xs">
                Ask the gate staff for the code after you&apos;ve paid via cash, static QR, or another method.
              </p>
            </div>

            <Input
              autoFocus
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={gateCode}
              onChange={(e) => setGateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              className="bg-[#0A0A0A] border-[#3A2F16] text-center text-2xl text-[#F2F2F2] text-mono tracking-[0.4em] h-14"
              data-testid="gate-code-input"
            />

            <Button
              type="submit"
              disabled={gateCode.length !== 6 || submitting}
              className="btn-gold w-full h-11"
              data-testid="gate-code-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              Verify code & confirm booking
            </Button>

            <p className="text-[#6F6F6F] text-[11px] text-center">
              The code must match the amount {formatCurrency(amount)} you paid at the gate.
            </p>
          </form>
        )}

        <p className="text-center text-[#4A4A4A] text-xs">
          {method === 'online'
            ? `Powered by ${gatewayLabel} · 256-bit secure encryption`
            : 'All payments are recorded for your booking history.'}
        </p>
      </div>
    </div>
  );
}
